import {
	AfterViewInit,
	Component,
	Input,
	OnDestroy,
	OnChanges,
	ViewChild,
	OnInit,
} from '@angular/core';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import {
	DataSetComponent,
	DropdownOption,
} from 'src/app/components/data-set/data-set.component';
import { ControlAssignment } from 'src/app/models/data/deviceControl';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { User } from 'src/app/models/data/user';
import { AssignDeviceControlsComponent } from 'src/app/ui/shared/assign-device-controls/assign-device-controls.component';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { AccessDatePipe } from 'src/app/components/pipes';
import {
	Device,
	DeviceAccessSource,
	hasOnlyPdqDevices,
	hasOnlySpecialGrantAccessDevices,
	hasSentriVaultDevices,
} from 'src/app/models/data/device';
import { UserProfile } from 'src/app/models/model';
import { DateTime } from 'luxon';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import {
	LoadingConfirmationDialogComponent,
	LoadingConfirmationDialogComponentData,
} from '../../../components/loading-confirmation-dialog/loading-confirmation-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetSelectedActionGroupDirective,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { DataSetSearchComponent } from '../../../components/data-set/data-set-search.component';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import {
	DataSetTableComponent,
	DataSetTableRowSelectionComponent,
	DataSetTableMenuComponent,
} from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatCheckbox } from '@angular/material/checkbox';
import { HasSystemCapabilityDirective } from '../../../directives/system-capabilities/has-system-capability.directive';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import {
	DeviceControlDaysPipe,
	DeviceControlDaysOnlyPipe,
	DeviceControlHoursOnlyPipe,
	ControlAssignmentDurationPipe,
} from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';
import {
	MatDatepicker,
	MatDatepickerInput,
	MatDatepickerToggle,
} from '@angular/material/datepicker';
import { MatSuffix } from '@angular/material/form-field';
import { formatDate } from '@angular/common';

@Component({
	selector: 'app-user-access',
	templateUrl: './user-access-tab.component.html',
	styleUrls: ['./user-access-tab.component.scss', '../../common/main.scss'],
	providers: [AccessDatePipe],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetSelectedActionGroupDirective,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCheckbox,
		MatCellDef,
		MatCell,
		DataSetTableRowSelectionComponent,
		HasSystemCapabilityDirective,
		MatSortHeader,
		MatIcon,
		MatTooltip,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		DeviceControlDaysPipe,
		DeviceControlDaysOnlyPipe,
		DeviceControlHoursOnlyPipe,
		ControlAssignmentDurationPipe,
		DataSetLoadingRowComponent,
	],
})
export class UserAccessTabComponent
	implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@Input() devices: {
		device: Device;
		groupNames?: string[];
		source?: DeviceAccessSource;
	}[] = [];
	@Input() companyId?: string;
	@Input() deviceGroupId?: string;
	@Input() deviceId?: string;
	@Input() hasAjaxDevice?: boolean;
	@Input() hasOnlyPDQ?: boolean;
	@Input() hasSentriVaultDevice?: boolean;

	localizeInherited: string = $localize`:@@userAccessTab-inherited:Inherited`;
	localizeTo: string = $localize`:@@userAccessTab-to:To`;
	localizeNone: string = $localize`:@@userAccessTab-none:None`;

	displayedColumns: string[] = [
		'rowSelect',
		'userName',
		'controlName',
		'source',
		'deviceGroupName',
		'duration',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<ControlAssignment>();

	filterSubscription?: Subscription;
	private selectionSubscription?: Subscription;
	private paginationSubscription?: Subscription;

	hasDeviceManagementSystemCapability: boolean;
	userProfile!: UserProfile;
	hasOwnedDevices = false;

	// Reactive checkbox state properties
	isAllSelected: boolean = false;
	isIndeterminate: boolean = false;

	deviceAccessTooltip = $localize`:@@userAccessTab-deviceAccessTooltip:Device Access`;
	installDeviceTooltip = $localize`:@@userAccessTab-installDeviceTooltip:Install Device`;
	removeDeviceTooltip = $localize`:@@userAccessTab-removeDeviceTooltip:Remove Device`;
	armDisarmAlarmTooltip = $localize`:@@userAccessTab-armDisarmAlarmTooltip:Arm/Disarm Alarm`;
	alarmOverrideTooltip = $localize`:@@userAccessTab-alarmOverrideTooltip:Alarm Override`;

	filterLabels = {
		defaultPlaceholder: $localize`:@@userAccessTab-filter-defaultPlaceholder:Search user name...`,
		userName: $localize`:@@userAccessTab-filterLabelUser:User`,
		controlName: $localize`:@@userAccessTab-filterLabelControlName:Control Name`,
		deviceGroupName: $localize`:@@userAccessTab-filterLabelDeviceGroupName:Device Group Name`,
		duration: $localize`:@@userAccessTab-filterOptionLabelDuration:Duration`,
		company: $localize`:@@userAccessTab-filterOptionLabelCompany:Company`,
		activeDate: $localize`:@@userAccessTab-filterOptionLabelActiveDate:Active Date`,
	};

	statusFilterOptions: DropdownOption[] = [
		{
			label: $localize`:@@userAccessTab-filterOptionActive:Active`,
			value: '1',
		},
		{
			label: $localize`:@@userAccessTab-filterOptionInactive:Inactive`,
			value: '0',
		},
	];

	getControlAssignmentsLoadingKey = ApiLoadingKeys.getControlAssignments;
	finishedLoadingInitData: boolean = false;

	hasOnlyPDQDevices: boolean = true; // true if a device is PDQ or device group only has PDQ
	hasSentriVaultDevices: boolean = false;
	/** True for Senturian/PDQ-only, SentriVault-only, or mixed of only those types. */
	hasOnlySpecialGrantAccessDevices: boolean = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly dialog: MatDialog,
		private readonly scService: SystemCapabilityService,
		private readonly accessDatePipe: AccessDatePipe,
		private readonly snackbar: MatSnackBar,
		public readonly loadingService: LoadingIndicatorService
	) {
		this.hasDeviceManagementSystemCapability = this.scService.has(
			SystemCapabilityEnum.deviceManagement
		);
	}

	private updateDeviceTypeFlags(): void {
		// Prioritize backend-calculated hasOnlyPDQ value over local calculation
		if (typeof this.hasOnlyPDQ === 'boolean') {
			this.hasOnlyPDQDevices = this.hasOnlyPDQ;
		} else {
			this.hasOnlyPDQDevices = hasOnlyPdqDevices(this.devices);
		}

		if (typeof this.hasSentriVaultDevice === 'boolean') {
			this.hasSentriVaultDevices = this.hasSentriVaultDevice;
		} else {
			this.hasSentriVaultDevices = hasSentriVaultDevices(this.devices);
		}

		this.hasOnlySpecialGrantAccessDevices = hasOnlySpecialGrantAccessDevices(
			this.devices ?? []
		);
	}

	async ngOnInit() {
		this.updateDeviceTypeFlags();
	}

	get editAccessLabel(): string {
		return $localize`:@@userAccess-editAccess:Edit Access`;
	}

	ngAfterViewInit(): void {
		this.dataSet?.applyFilters();
		this.filterSubscription = this.dataSet?.queryParams.subscribe(
			async (filters) => {
				const nextFilters = filters.map((filter): QueryParam =>
					filter.value instanceof Date
						? {
								...filter,
								value: formatDate(filter.value, 'yyyy-MM-dd', 'en-US'),
							}
						: filter
				);
				if (this.deviceGroupId) {
					nextFilters.push({
						param: 'deviceGroupId',
						value: this.deviceGroupId,
					});
				} else if (this.deviceId) {
					nextFilters.push({ param: 'deviceIds', value: [this.deviceId] });
					nextFilters.push({ param: 'deviceStatus', value: 'active' });
				}
				const result = await this.apiService.getControlAssignments([
					...nextFilters,
					{ param: 'type', value: 'user' },
				]);
				this.finishedLoadingInitData = true;
				this.dataSource.data = result;
				if (!this.dataSet.selection.isEmpty()) {
					const selectedIds = this.dataSet.selection.selected.map(
						(it) => it.id
					);
					const selectedItems = result.filter((it) =>
						selectedIds.includes(it.id)
					);
					this.dataSet.clearAllSelections();
					this.dataSet.selection.select(...selectedItems);
				}
				this.updateCheckboxState();
			}
		);

		// Subscribe to selection changes to update checkbox state reactively
		this.selectionSubscription = this.dataSet.selectedChanged.subscribe(() => {
			this.updateCheckboxState();
		});

		// Subscribe to pagination changes to update checkbox state when switching pages
		this.paginationSubscription =
			this.dataSet.presentationFilterChanged.subscribe(() => {
				this.updateCheckboxState();
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;

		this.selectionSubscription?.unsubscribe();
		this.selectionSubscription = undefined;

		this.paginationSubscription?.unsubscribe();
		this.paginationSubscription = undefined;

		this.dialog.closeAll();
	}

	ngOnChanges(): void {
		const ownedDevices = this.devices.filter(
			(device) => device.source?.companyId === this.companyId
		);
		this.hasOwnedDevices = ownedDevices.length > 0;

		this.updateDeviceTypeFlags();
	}

	formatUserName(user: User): string {
		return `${user.firstName} ${user.lastName} (${user.email})`;
	}

	formatDuration(control: ControlAssignment): string {
		return control.isSystemAssignment
			? this.localizeInherited
			: `${this.accessDatePipe.transform(control.utcStartDate)} ${this.localizeTo} ${this.accessDatePipe.transform(control.utcEndDate) ?? this.localizeNone}`;
	}

	isActive(control: ControlAssignment): boolean {
		const startDate = DateTime.fromISO(control.utcStartDate, { zone: 'UTC' });
		const endDate = DateTime.fromISO(
			control.utcEndDate ?? '2500-12-31T23:59:59.999Z',
			{ zone: 'UTC' }
		);
		const currentDate = DateTime.utc();

		return startDate <= currentDate && endDate > currentDate;
	}

	onGrantAccessClicked(): void {
		const data = {
			deviceGroupId: this.deviceGroupId,
			deviceId: this.deviceId,
			assignmentType: 'user',
			devices: this.devices,
			companyId: this.companyId,
			hasAjaxDevice: this.hasAjaxDevice,
			hasOnlyPDQ: this.hasOnlyPDQ,
			hasSentriVaultDevices: this.hasSentriVaultDevices,
			hasOnlySpecialGrantAccessDevices: this.hasOnlySpecialGrantAccessDevices,
		};
		this.dialog
			.open(AssignDeviceControlsComponent, {
				data,
				panelClass: 'drawer-dialog',
			})
			.afterClosed()
			.subscribe((_: any) => this.dataSet.applyFilters());
	}

	onDeleteControlAssignmentsClicked(): void {
		const controlAssignmentIds = this.dataSet.selection.selected.map(
			(it: ControlAssignment) => it.id
		);
		this.deleteControlAssignments(controlAssignmentIds);
	}

	onEditControlAssignmentsClicked(
		controlAssignments?: ControlAssignment[]
	): void {
		const data = {
			deviceGroupId: this.deviceGroupId,
			deviceId: this.deviceId,
			assignmentType: 'user',
			devices: JSON.parse(JSON.stringify(this.devices)),
			companyId: this.companyId,
			hasAjaxDevice: this.hasAjaxDevice,
			hasOnlyPDQ: this.hasOnlyPDQ,
			hasSentriVaultDevices: this.hasSentriVaultDevices,
			hasOnlySpecialGrantAccessDevices: this.hasOnlySpecialGrantAccessDevices,
			// Edit mode specific data
			isEditMode: true,
			controlAssignments: JSON.parse(
				JSON.stringify(controlAssignments || this.dataSet.selection.selected)
			),
			showCompanyMessage: true,
		};
		this.dialog
			.open(AssignDeviceControlsComponent, {
				data,
				panelClass: 'drawer-dialog',
			})
			.afterClosed()
			.subscribe((result: any) => {
				if (!!result) {
					this.dataSet.applyFilters();
				}
			});
	}

	onRowMenuDeleteClicked(controlAssignment: ControlAssignment): void {
		this.deleteControlAssignments([controlAssignment.id]);
	}

	onRowMenuEditClicked(controlAssignment: ControlAssignment): void {
		this.onEditControlAssignmentsClicked([controlAssignment]);
	}

	deleteControlAssignments(controlAssignmentIds: string[]): void {
		const data: LoadingConfirmationDialogComponentData = {
			apiFunctionToExecute: () =>
				this.apiService.deleteControlAssignments(controlAssignmentIds),
			loadingKeyOfApiFunction: ApiLoadingKeys.deleteControlAssignments,

			headerIcon: 'sl-danger-circle',
			headerTextBeforeLoading: $localize`:@@userAccessTab-confirm:Confirm`,
			bodyTextBeforeLoading:
				$localize`:@@userAccessTab-removalConfirmationAltText:This action will remove access for` +
				` ${controlAssignmentIds.length} ` +
				(controlAssignmentIds.length === 1
					? $localize`:@@userAccessTab-deletionConfirmationText2:user to the connected device. This cannot be restored. Are you sure?`
					: $localize`:@@userAccessTab-deletionConfirmationText3:users to the connected device. This cannot be restored. Are you sure?`),
			negativeButtonDialogText: $localize`:@@userAccessTab-cancel:Cancel`,
			positiveButtonDialogText: $localize`:@@userAccessTab-remove:Remove`,

			negativeButtonClass: 'secondary-small-button',
			positiveButtonClass: 'warning-small-button',
		};

		const dialogRef: MatDialogRef<LoadingConfirmationDialogComponent> =
			this.dialog.open(LoadingConfirmationDialogComponent, {
				maxWidth: '392px',
				panelClass: 'pop-up-dialog-smaller-v2',
				data: data,
			});

		dialogRef.afterClosed().subscribe(async (result: any) => {
			if (!!result) {
				if (controlAssignmentIds.length === 1) {
					this.snackbar.open(
						$localize`:@@userAccessTab-confirmDeletedAccessMsg:Successfully deleted access`,
						undefined,
						{
							duration: 2000,
						}
					);
				} else {
					this.snackbar.open(
						$localize`:@@userAccessTab-confirmDeletedAccessesMsg:Successfully deleted accesses`,
						undefined,
						{
							duration: 2000,
						}
					);
				}

				this.dataSet.clearAllSelections();
				this.dataSet.applyFilters();
			}
		});
	}

	private updateCheckboxState(): void {
		if (!this.dataSet?.selection || !this.dataSource?.data) {
			this.isAllSelected = false;
			this.isIndeterminate = false;
			return;
		}

		// Get current page data for pagination support
		const currentPageData = this.getCurrentPageData();
		const selectableItems = currentPageData.filter((access) =>
			this.isAccessRecordSelectable(access)
		);

		if (selectableItems.length === 0) {
			this.isAllSelected = false;
			this.isIndeterminate = false;
			return;
		}

		// Count how many selectable items on the current page are selected
		const selectedOnCurrentPage = selectableItems.filter((item) =>
			this.dataSet.selection.isSelected(item)
		).length;

		this.isAllSelected =
			selectedOnCurrentPage > 0 &&
			selectedOnCurrentPage === selectableItems.length;
		this.isIndeterminate =
			selectedOnCurrentPage > 0 &&
			selectedOnCurrentPage < selectableItems.length;
	}

	private getCurrentPageData(): ControlAssignment[] {
		if (!this.dataSource?.data) {
			return [];
		}

		// Check if paginator exists and get current page data
		if (this.dataSource.paginator) {
			const startIndex =
				this.dataSource.paginator.pageIndex *
				this.dataSource.paginator.pageSize;
			const endIndex = startIndex + this.dataSource.paginator.pageSize;
			return this.dataSource.data.slice(startIndex, endIndex);
		}

		// If no paginator, return all data
		return this.dataSource.data;
	}

	isSelectedAllChecked(): boolean {
		return this.isAllSelected;
	}

	isSelectAllIntermediate(): boolean {
		return this.isIndeterminate;
	}

	// Check how many total can be selected/editable on current page
	countTotalSelectableElements() {
		const currentPageData = this.getCurrentPageData();
		let totalSelectableCount = 0;
		currentPageData.forEach((access) => {
			if (this.isAccessRecordSelectable(access)) {
				totalSelectableCount++;
			}
		});
		return totalSelectableCount;
	}

	onSelectAllToggle(checked: boolean): void {
		if (!this.dataSet?.selection) {
			return;
		}

		const currentPageData = this.getCurrentPageData();
		const selectableItems = currentPageData.filter((access) =>
			this.isAccessRecordSelectable(access)
		);

		if (selectableItems.length === 0) {
			return;
		}

		if (checked) {
			// Select only selectable items on the current page
			this.dataSet.selection.select(...selectableItems);
		} else {
			// Deselect only selectable items on the current page
			this.dataSet.selection.deselect(...selectableItems);
		}
	}

	formatSelectedUserName(item: ControlAssignment): string {
		const firstName = item?.entityRelationship?.childUser?.firstName || '';
		const lastName = item?.entityRelationship?.childUser?.lastName || '';
		const lastNameInitial = lastName.charAt(0).toUpperCase();

		if (firstName && lastNameInitial) {
			return `${firstName} ${lastNameInitial}`;
		} else if (firstName) {
			return firstName;
		} else if (lastName) {
			return lastName;
		}

		return (
			item?.entityRelationship?.childUser?.email ||
			$localize`:@@selectedDisplayFormat-unknownUser:Unknown User`
		);
	}

	rowId(row: ControlAssignment): string {
		return row?.id || 'unknown-id';
	}

	/**
	 * Determines if an access record should be selectable/editable.
	 * When viewing from a device page (deviceId is set), only allow selection
	 * if the device group name matches the device's serial number (system-generated groups).
	 */
	private isAccessRecordSelectable(access: ControlAssignment): boolean {
		if (!access.canEditAccess) {
			return false;
		}

		// If the access is expired, and if so the user shouldn't be allowed to edit that selection
		const currentDate = DateTime.utc();
		const endDate = DateTime.fromISO(
			access.utcEndDate ?? '2500-12-31T23:59:59.999Z',
			{ zone: 'UTC' }
		);
		if (endDate < currentDate) {
			return false;
		}

		// If not viewing from a specific device page, all editable records are selectable
		if (!this.deviceId) {
			return true;
		}

		// Find the specific device that matches the current deviceId
		const currentDevice = this.devices[0];
		if (!currentDevice) {
			return false;
		}

		// Only allow selection if device group name matches device serial number
		// (indicating a system-generated device group, not a user-created one)
		return currentDevice.device.serialNumber === access.deviceGroup?.name;
	}

	// Determines if an access record should be selectable/editable
	isSelectable(access: ControlAssignment): boolean {
		return this.isAccessRecordSelectable(access);
	}
}
